import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { API, errMsg } from "@/lib/api";

const PERKS = ["Every free SC & no-deposit offer we track", "Updated & re-verified every single day", "Promo codes + direct claim links", "One-time $3 — lifetime access, no subscription"];

export const PaywallDialog = ({ open, onOpenChange }) => {
  const [email, setEmail] = useState("");
  const [mode, setMode] = useState("buy");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "buy") {
        localStorage.setItem("pending_email", email);
        const { data } = await axios.post(`${API}/payments/checkout`, { email, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      const { data } = await axios.post(`${API}/access/verify`, { email });
      localStorage.setItem("vault_token", data.token);
      toast.success("Welcome back — vault unlocked");
      navigate("/vault");
    } catch (err) {
      toast.error(errMsg(err));
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F131D]/95 backdrop-blur-xl border-emerald-500/20 text-white sm:max-w-md" data-testid="paywall-dialog">
        <DialogHeader>
          <div className="h-11 w-11 rounded-xl bg-emerald-500/15 text-emerald-400 grid place-items-center mb-2"><Lock size={20} /></div>
          <DialogTitle className="font-display text-2xl">{mode === "buy" ? "Unlock the full vault" : "Restore your access"}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {mode === "buy" ? "Pay once with card via Stripe. Your access is tied to your email." : "Enter the email you used at checkout."}
          </DialogDescription>
        </DialogHeader>
        {mode === "buy" && (
          <ul className="space-y-2 my-2">
            {PERKS.map((p) => (
              <li key={p} className="flex gap-2 text-sm text-slate-300"><Check size={16} className="text-emerald-400 mt-0.5 shrink-0" />{p}</li>
            ))}
          </ul>
        )}
        <form onSubmit={submit} className="space-y-3">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="paywall-email-input" className="h-12 bg-[#07090E] border-white/10" />
          <button type="submit" disabled={loading} data-testid="paywall-unlock-button" className="w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-[#07090E] font-bold flex items-center justify-center gap-2 transition-colors">
            {loading && <Loader2 size={16} className="animate-spin" />}
            {mode === "buy" ? "Pay $3.00 & Unlock" : "Restore Access"}
          </button>
        </form>
        <button onClick={() => setMode(mode === "buy" ? "restore" : "buy")} data-testid="paywall-toggle-mode" className="text-xs text-slate-400 hover:text-emerald-400 transition-colors">
          {mode === "buy" ? "Already paid? Restore access with your email" : "New here? Buy lifetime access for $3"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
